/** The device test battery: what a Quest session measures, in order. */

/**
 * How the bundle is put on screen.
 *  - batched: one BatchedMesh per system, multi-draw (the client's path)
 *  - loop:    the same batches with WEBGL_multi_draw hidden from three.js, so
 *             it draws one part per call through its `_gl_DrawID` fallback
 *             (no `#extension GL_ANGLE_multi_draw` in the shader)
 *  - merged:  one plain Mesh per system, no batching at all (the control)
 */
export type RenderMode = "batched" | "loop" | "merged";

export interface CaseSpec {
  id: string;
  title: string;
  level: string;
  batch: "stable" | "default";
  churn: boolean;
  cull: boolean;
  systems?: string[];
  render?: RenderMode;
  /**
   * Merge adjacent slots that share geometry adjacency and instance data into
   * one sub-draw. Default true; false is the A/B against session 2's numbers,
   * where every part cost a sub-draw of its own.
   */
  merge?: boolean;
  /** Seconds of frames to collect after a one-second settle. */
  seconds: number;
  /** What the tester should look for. */
  watch: string;
}

const FAR_STABLE: CaseSpec = { id: "far-stable", title: "far, stable batches", level: "far", batch: "stable", churn: false, cull: true, seconds: 8, watch: "baseline: the whole body at the far level" };
const FAR_LOOP: CaseSpec = { id: "far-loop", title: "far, batches, multi-draw off (loop)", level: "far", batch: "stable", churn: false, cull: true, render: "loop", seconds: 6, watch: "the same batches drawn one part per call: a body here but not in the baseline means the multi-draw shader path is at fault" };
const FAR_MERGED: CaseSpec = { id: "far-merged", title: "far, merged meshes (no batching)", level: "far", batch: "stable", churn: false, cull: true, render: "merged", seconds: 6, watch: "one plain mesh per system, the control: no body here either means the fault is not batching" };
const FAR_DEFAULT_CHURN: CaseSpec = { id: "far-default-churn", title: "far, default, visibility churn", level: "far", batch: "default", churn: true, cull: true, seconds: 8, watch: "the hazard case: wrong parts drawn for a frame shows as flicker or colour swaps" };

export const PLANS: Record<string, CaseSpec[]> = {
  full: [
    FAR_STABLE,
    { id: "far-stable-nomerge", title: "far, stable batches, run merge off", level: "far", batch: "stable", churn: false, cull: true, merge: false, seconds: 8, watch: "session 2's baseline: one sub-draw per part. Against far-stable this is the whole of the run-merge change, on the same headset in the same session" },
    FAR_LOOP,
    FAR_MERGED,
    { id: "far-default", title: "far, default BatchedMesh", level: "far", batch: "default", churn: false, cull: true, seconds: 8, watch: "same scene through three.js's default compaction: any flicker as you turn your head?" },
    { id: "far-stable-churn", title: "far, stable, visibility churn", level: "far", batch: "stable", churn: true, cull: true, seconds: 8, watch: "50 parts hidden and shown each frame; the stable slots should not flicker" },
    FAR_DEFAULT_CHURN,
    { id: "far-stable-nocull", title: "far, stable, no per-frame culling", level: "far", batch: "stable", churn: false, cull: false, seconds: 6, watch: "the cost of culling itself: compare frame time with the baseline" },
    { id: "near-stable", title: "near, stable batches", level: "near", batch: "stable", churn: false, cull: true, seconds: 8, watch: "the Quest 3 default quality: does it hold 72 Hz?" },
    { id: "near-default-churn", title: "near, default, visibility churn", level: "near", batch: "default", churn: true, cull: true, seconds: 6, watch: "the hazard at the near level" },
    { id: "far-skeleton", title: "far, skeleton only", level: "far", batch: "stable", churn: false, cull: true, systems: ["skeletal"], seconds: 6, watch: "one batch: the floor of the draw-call cost" },
    { id: "hero-stable", title: "hero, stable batches (stress)", level: "hero", batch: "stable", churn: false, cull: true, seconds: 6, watch: "the whole body at hero, which the client never draws: a stress reading only" },
  ],
  quick: [
    { ...FAR_STABLE, seconds: 2 },
    { ...FAR_LOOP, seconds: 2 },
    { ...FAR_MERGED, seconds: 2 },
    { ...FAR_DEFAULT_CHURN, seconds: 2, watch: "the hazard case" },
  ],
};
